import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { EmptyState } from '@/components/EmptyState';
import { Plus, Edit, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { ROLE_LABEL, ROLE_BADGE } from '@/lib/auth';
import { cn } from '@/lib/utils';

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [opds, setOpds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, mode: 'create', target: null });
  const empty = { email: '', full_name: '', role: 'operator', jabatan: '', opd_id: '', nip: '', phone: '', whatsapp_number: '', password: 'ProAktif2026!' };
  const [form, setForm] = useState(empty);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/users'), api.get('/opd')]).then(([u, o]) => { setUsers(u.data); setOpds(o.data); }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setForm(empty); setModal({ open: true, mode: 'create', target: null }); };
  const openEdit = (u) => { setForm({ ...u, password: '' }); setModal({ open: true, mode: 'edit', target: u }); };

  const save = async () => {
    try {
      if (modal.mode === 'create') {
        await api.post('/users', form);
        toast.success('User berhasil dibuat');
      } else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.email;
        await api.patch(`/users/${modal.target.id}`, payload);
        toast.success('User berhasil diperbarui');
      }
      setModal({ open: false });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus user ${u.full_name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success('User dihapus');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal');
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      load();
    } catch (e) {
      toast.error('Gagal');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="User Management"
        subtitle="Kelola akun pengguna sistem PROAKTIF."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'Manajemen' }, { label: 'User' }]}
        action={<Button data-testid="add-user-button" onClick={openCreate} className="bg-[#DC2626] hover:bg-[#B91C1C]"><Plus className="w-4 h-4 mr-2" />Tambah User</Button>}
      />

      <Card className="border-slate-200 shadow-premium overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" /></div>
        ) : users.length === 0 ? (
          <EmptyState title="Belum ada user" icon={Users} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/60">
                  <TableHead>Nama</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="w-40">Role</TableHead>
                  <TableHead className="hidden md:table-cell">OPD</TableHead>
                  <TableHead className="w-24">Aktif</TableHead>
                  <TableHead className="w-28 text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>
                      <div className="font-semibold">{u.full_name}</div>
                      <div className="text-xs text-slate-500">{u.jabatan}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{u.email}</TableCell>
                    <TableCell><span className={cn('text-xs px-2 py-1 rounded font-semibold', ROLE_BADGE[u.role])}>{ROLE_LABEL[u.role]}</span></TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-slate-600">{u.opd_nama || '-'}</TableCell>
                    <TableCell><Switch checked={u.is_active} onCheckedChange={() => toggleActive(u)} /></TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(u)}><Edit className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(u)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={modal.open} onOpenChange={(o) => setModal({ ...modal, open: o })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{modal.mode === 'create' ? 'Tambah User' : 'Edit User'}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Nama Lengkap</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} disabled={modal.mode === 'edit'} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="operator">Operator Prokopim</SelectItem>
                  <SelectItem value="kabag">Kabag Prokopim</SelectItem>
                  <SelectItem value="pimpinan">Pimpinan</SelectItem>
                  <SelectItem value="delegasi">Pejabat Delegasi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Jabatan</Label>
              <Input value={form.jabatan} onChange={(e) => setForm({ ...form, jabatan: e.target.value })} />
            </div>
            <div>
              <Label>OPD</Label>
              <Select value={form.opd_id || 'none'} onValueChange={(v) => setForm({ ...form, opd_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Pilih OPD" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">- Tidak ada -</SelectItem>
                  {opds.map((o) => <SelectItem key={o.id} value={o.id}>{o.nama}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>NIP</Label>
              <Input value={form.nip} onChange={(e) => setForm({ ...form, nip: e.target.value })} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label className="flex items-center gap-1.5">No. WhatsApp <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold uppercase">WA</span></Label>
              <Input value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} placeholder="628xxxxxxxxxx" />
            </div>
            <div className="md:col-span-2">
              <Label>Password {modal.mode === 'edit' && <span className="text-xs text-slate-500">(kosongkan jika tidak diganti)</span>}</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal({ open: false })}>Batal</Button>
            <Button onClick={save} className="bg-[#DC2626] hover:bg-[#B91C1C]">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
